import type { ProductUpdateRequest } from '@/api/handlers/resources/products/requests';
import type { ProductDetail, ProductDraftIssue } from '@/api/models';
import {
    moneyParam,
    noticeFor,
    rowsParam,
    type CatalogImportNotice,
} from '@/pages/catalogImport';

/**
 * Pure helpers for a draft's panel on the product page (bos-app#225, US-18,
 * AC-17): the issues as the wizard's notice sentences with their rows, the
 * publish request, and what a refused publish says.
 */

/**
 * One thing the import left out for an issue, as a sentence key under
 * `pages` and its params as text: the panel translates it after the issue's
 * sentence.
 */
export type DraftIssueKept = {
    key: string;
    params: Record<string, string>;
};

/** One `draftIssues` entry, ready for the panel to render. */
export type DraftIssueItem = {
    /** Stable within one product's list: the entry's code and position. */
    id: string;
    /** The catalog import's sentence for the code (`renderNotice` renders it). */
    notice: CatalogImportNotice;
    /** The file rows it came from, as the sentences print rows (`212, 213`). */
    rows: string;
    /** How many rows, for the "Row" / "Rows" plural; `0` reads as no row. */
    rowCount: number;
    /** What the import left out, so the owner can restore it; `[]` when nothing. */
    kept: DraftIssueKept[];
};

/** Why a publish was refused, as far as the page can tell. */
export type DraftPublishRefusal =
    /**
     * No category: the update requires one, so the page does not send it
     * (review of bos-app#229, finding 3). The owner chooses one with Edit.
     */
    | { kind: 'noCategory' }
    /** A variant still sells at 0: the one rule the update adds for a draft. */
    | { kind: 'unpricedVariant'; uniqueKey: string }
    /** Any other 400: the page has no field to point at. */
    | { kind: 'other' };

const nonBlank = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value : null;

/** `droppedOptions` (`{ attributeType, value }[]`) as `Colour: Black, Storage`. */
const optionsText = (value: unknown): string | null => {
    if (!Array.isArray(value)) {
        return null;
    }
    const options = value.flatMap((option: unknown) => {
        if (typeof option !== 'object' || option === null) {
            return [];
        }
        const { attributeType, value: picked } = option as {
            attributeType?: unknown;
            value?: unknown;
        };
        const type = nonBlank(attributeType);
        const text = nonBlank(picked);
        if (type === null) {
            return text === null ? [] : [text];
        }
        return [text === null ? type : `${type}: ${text}`];
    });
    return options.length === 0 ? null : options.join(', ');
};

/**
 * `SKU_REQUIRED`'s `params.barcode` when the row had no barcode either: the
 * word, not the code the import generated.
 */
const GENERATED_BARCODE = 'generated';

/** bos-api names a keeper without a SKU by its row: `Row 16`. */
const KEEPER_ROW = /^Row (\d+)$/;

/**
 * What the import's fallback left out for one issue (bos-api API-195), from
 * the extras its `params` keep "so the owner can restore them" (review of
 * bos-app#229, finding 26):
 *
 * - `droppedWholesalePrice`: a bad sale price was written as 0 and the
 *   wholesale price dropped with it;
 * - `droppedOptions`: the line's attributes, dropped for a simple product,
 *   other axes, a missing or ambiguous category;
 * - `VALUE_TOO_LONG`'s `value`: the whole text the cell was cut from;
 * - `SKU_BARCODE_CONFLICT`'s `barcode`: not written, it is another
 *   product's;
 * - `BARCODE_SHARED_ACROSS_SKUS`'s `keptBy`: another SKU (or row) kept the
 *   barcode, so this line did not get it;
 * - the barcode fallbacks (bos-app#232, rule 8), each with a generated
 *   barcode: an IMEI or not-digits `barcode` moved to the blank Lot number
 *   (`lotNumber` present) or dropped because the row's lot number was set (no
 *   `lotNumber`); a scientific-notation `value` dropped; and `SKU_REQUIRED`
 *   with `barcode: "generated"`, a fixed word that is never printed as a
 *   code (the code is on the variant).
 */
const keptOf = (issue: ProductDraftIssue): DraftIssueKept[] => {
    const { code, params } = issue;
    const kept: DraftIssueKept[] = [];
    if (typeof params.droppedWholesalePrice === 'number') {
        kept.push({
            key: 'products.detail.draft.kept.droppedWholesalePrice',
            params: { amount: moneyParam(params.droppedWholesalePrice) },
        });
    }
    const options = optionsText(params.droppedOptions);
    if (options !== null) {
        kept.push({
            key: 'products.detail.draft.kept.droppedOptions',
            params: { options },
        });
    }
    const whole = nonBlank(params.value);
    if (code === 'VALUE_TOO_LONG' && whole !== null) {
        kept.push({
            key: 'products.detail.draft.kept.wholeText',
            params: { value: whole },
        });
    }
    const barcode = nonBlank(params.barcode);
    if (code === 'SKU_BARCODE_CONFLICT' && barcode !== null) {
        kept.push({
            key: 'products.detail.draft.kept.barcodeNotWritten',
            params: { barcode },
        });
    }
    const keeper = nonBlank(params.keptBy);
    if (
        code === 'BARCODE_SHARED_ACROSS_SKUS' &&
        barcode !== null &&
        keeper !== null
    ) {
        const row = KEEPER_ROW.exec(keeper)?.[1];
        kept.push(
            row === undefined
                ? {
                      key: 'products.detail.draft.kept.barcodeKeptBy',
                      params: { barcode, sku: keeper },
                  }
                : {
                      key: 'products.detail.draft.kept.barcodeKeptByRow',
                      params: { barcode, row },
                  },
        );
    }
    if (
        (code === 'BARCODE_LOOKS_LIKE_IMEI' || code === 'BARCODE_NOT_DIGITS') &&
        barcode !== null
    ) {
        kept.push(
            nonBlank(params.lotNumber) === null
                ? {
                      key: 'products.detail.draft.kept.barcodeDropped',
                      params: { barcode },
                  }
                : {
                      key: 'products.detail.draft.kept.barcodeMovedToLot',
                      params: { barcode },
                  },
        );
    }
    if (
        code === 'BARCODE_SCIENTIFIC_NOTATION' &&
        whole !== null &&
        !issue.fields.includes('lotNumber')
    ) {
        kept.push({
            key: 'products.detail.draft.kept.scientificDropped',
            params: { value: whole },
        });
    }
    if (code === 'SKU_REQUIRED' && params.barcode === GENERATED_BARCODE) {
        kept.push({
            key: 'products.detail.draft.kept.barcodeGenerated',
            params: {},
        });
    }
    return kept;
};

/**
 * The issues in the server's order, each as the sentence the import wizard
 * shows for its code (same params and fields), plus the rows it came from
 * and what the import left out (`keptOf`).
 */
export const draftIssueItemsOf = (
    issues: readonly ProductDraftIssue[],
): DraftIssueItem[] =>
    issues.map((issue, index) => ({
        id: `${issue.code}-${index}`,
        notice: noticeFor({
            code: issue.code,
            params: issue.params,
            fields: issue.fields,
        }),
        rows: rowsParam(issue.rows),
        rowCount: issue.rows.length,
        kept: keptOf(issue),
    }));

/**
 * `PUT /products/{id}` that publishes the product as it is stored: its own
 * fields as the detail has them, `draft: false`, and no variant. A variant the
 * request leaves out keeps every stored field (the server then checks its
 * stored sale price), so publishing changes nothing else; sending the variants
 * would also clear what the edit form does not send (the sale end date, the
 * hidden discount price).
 *
 * `null` for a product with no category: the update requires one, so the
 * request would always be refused (`publishRefusalOf` says why).
 */
export const publishRequestOf = (
    product: ProductDetail,
): ProductUpdateRequest | null => {
    if (!product.category) {
        return null;
    }
    const request: ProductUpdateRequest = {
        name: product.name,
        unit: product.unit,
        vatType: product.vatType,
        categoryId: product.category.id,
        draft: false,
        variants: [],
    };
    if (product.sku) {
        request.sku = product.sku;
    }
    if (product.brand) {
        request.brandId = product.brand.id;
    }
    return request;
};

/**
 * Why a publish is refused for this product: before sending, the missing
 * category; after a 400, what the page can see in its own data. A missing
 * category comes first, so the 0-price reason is never guessed for a refusal
 * the category explains. Otherwise the server refuses while a variant would
 * sell at 0 and names it only in its English message, so the page finds that
 * variant in its own data instead of reading the message (rulebook §7); with
 * none, the refusal is some other problem.
 */
export const publishRefusalOf = (
    product: ProductDetail,
): DraftPublishRefusal => {
    if (!product.category) {
        return { kind: 'noCategory' };
    }
    const unpriced = product.variants.find(
        (variant) => variant.salePriceTetri <= 0,
    );
    return unpriced
        ? { kind: 'unpricedVariant', uniqueKey: unpriced.uniqueKey }
        : { kind: 'other' };
};
